import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
// replace imports from `@mui/material` to `@mui/material` - mui updated branding
import { IconButton, TextField, Button, Badge } from '@mui/material';

import {
  DataGridPro,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarExport,
  useGridApiRef
} from './x-data-grid-pro';

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { createTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const defaultTheme = createTheme();
const useStyles = makeStyles(
  (theme) =>
    createStyles({
      root: {
        padding: theme.spacing(0.5, 0.5, 0),
        justifyContent: 'space-between',
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        fontColor: '#000',
        backgroundColor: '#fff',
      },
      filterText: {
        color: '#000',
      },
      textField: {
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
        margin: theme.spacing(1, 2, 1.5),
        '& .MuiSvgIcon-root': {
          marginRight: theme.spacing(0.5),
        },
        '& .MuiInput-underline:before': {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    }),
  { defaultTheme },
);

function QuickSearchToolbar(props) {
  const classes = useStyles();
  let addButton = '';
  if (props.addItem) {
    addButton = props.addItem;
  }
  return (
    <div className={classes.root}>
      <div style={{ padding: 12 }}>
        {addButton}
        <GridToolbarColumnsButton className={classes.filterText} />
        <GridToolbarFilterButton
          componentsProps={{
            button: {
              startIcon: (
                <Badge
                  badgeContent={
                    props.filterModel.items.filter((item) => {
                      return item.value && item.value.length > 0;
                    }).length || 0
                  }
                  color="primary"
                  overlap="rectangular"
                >
                  <FilterListIcon />
                </Badge>
              ),
            },
          }}
          className={classes.filterText}
        />
        <GridToolbarDensitySelector className={classes.filterText} />
        <GridToolbarExport
          csvOptions={{ allColumns: true }}
          className={classes.filterText}
        />
        {props.selection && props.selection.length > 0 ? (
          <Button
            color="primary"
            variant="contained"
            onClick={() => props.deleteItems(props.selection)}
          >
            Delete Selected
          </Button>
        ) : (
          ''
        )}
      </div>
      <TextField
        variant="standard"
        value={props.value}
        onChange={props.onChange}
        placeholder="Search…"
        className={classes.textField}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear"
              size="small"
              style={{ visibility: props.value ? 'visible' : 'hidden' }}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
    </div>
  );
}

QuickSearchToolbar.propTypes = {
  clearSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default function QuickFilteringGrid(props) {
  let data = {
    rows: props.rows,
    rowLength: 100,
    maxColumns: 6,
    columns: props.columns
  };

  const [searchText, setSearchText] = React.useState('');
  const [pageSize, setPageSize] = React.useState(25);
  const [selection, setSelection] = React.useState([]);
  const [rows, setRows] = React.useState(props.rows);
  const [columns, setColumns] = React.useState(data.columns);

  const apiRef = useGridApiRef();

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = data.rows.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field].toString());
      });
    });
    setRows(filteredRows);
  };

  const deleteRows = (reportIds) => {
    setSelection([]);
    let newRows = rows.filter((item) => !reportIds.includes(item.id));
    setRows(newRows);
    if (props.deleteItems) {
      props.deleteItems(reportIds);
    }
  };

  useEffect(() => {
    setSelection([]);
    let newRows = [...props.rows];
    setRows(newRows);
    apiRef.current.updateRows(newRows);
  }, [props.rows]);

  const [filterModel, setFilterModel] = React.useState({
    items: [props.filter],
  });
  let checkboxSelect = false;
  if (props.checkboxSelection) {
    checkboxSelect = true;
  }

  return (
    <div>
      <DataGridPro
        components={{ Toolbar: QuickSearchToolbar }}
        rows={rows}
        columns={columns}
        disableSelectionOnClick={true}
        density={'comfortable'}
        pageSize={pageSize}
        onPageSizeChange={(pageSize) => setPageSize(pageSize)}
        rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        pagination
        filterModel={filterModel}
        onFilterModelChange={(model) => setFilterModel(model)}
        autoHeight={!props.showLoadingRing || true}
        apiRef={apiRef}
        checkboxSelection={checkboxSelect}
        onSelectionModelChange={(newSelection) => {
          setSelection(newSelection);
        }}
        selectionModel={selection}
        disableColumnMenu={false}
        componentsProps={{
          toolbar: {
            filterModel: filterModel,
            value: searchText,
            deleteItems: (reportIds) => deleteRows(reportIds),
            addItem: props.addItem,
            selection: selection,
            onChange: (event) => requestSearch(event.target.value),
            clearSearch: () => requestSearch(''),
          },
        }}
      />
    </div>
  );
}
